import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { EMAIL } from 'constants/messageMediums';
import { TemplateSource, TemplateType } from 'constants/templateTypes';
import { AllMessageMedium, CampaignType } from 'modules/domains/campaign';
import { PageSize } from 'ui/pageSize';
import { Pagination } from 'ui/pagination';
import { Skeleton } from 'ui/skeleton';
import { noop } from 'utils/noop';

import { TemplateTiles } from './components';
import { fetchDataFeeds, fetchMemberData, fetchTemplates } from './ducks/actions';
import {
  getFetchingTemplates,
  getTemplatesListQueryIds,
  getTemplatesListQueryTotals,
  selectTemplatesByQuery,
} from './ducks/selectors';
import {
  getTemplatesLocationQueryString,
  getUniqueMemberIds,
  hasRenderedTemplatesInRedux,
  parseTemplateTypeFilterSelection,
} from './utils';

const Footer = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

type TemplatePickerProps = {
  campaignType?: CampaignType;
  filter: string;
  forceRefresh?: boolean;
  hasError?: boolean;
  messageMedium: AllMessageMedium;
  onSelectDataFeed?: () => void;
  onSelectTemplate?: (template: any) => void;
  placementId?: number;
  selectedTab: TemplateSource;
  templateId?: number; // selected template id
  templateTypeFilter?: TemplateType[];
  useDataFeeds?: boolean;
};

export const TemplatePicker = ({
  campaignType,
  filter = '',
  forceRefresh = false,
  hasError = false,
  messageMedium = EMAIL,
  onSelectDataFeed = noop,
  onSelectTemplate = noop,
  placementId,
  selectedTab = TemplateSource.Base,
  templateId,
  templateTypeFilter = [],
  useDataFeeds = false,
}: TemplatePickerProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchDataFeeds());
  }, []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  // these constants will become dynamic in a future PR.
  const orderBy = 'updatedAtRaw';
  const sorting = 'desc';

  const whatsAppTemplateType = parseTemplateTypeFilterSelection({
    messageMedium,
    campaignType,
    templateTypeFilter,
  });

  // the templates to display are stored in redux via a query string that
  // takes into account all of the createWizardState params.
  const reduxStateString = getTemplatesLocationQueryString({
    filter,
    messageMedium,
    orderBy,
    page,
    pageSize,
    sorting,
    templateType: selectedTab,
    whatsAppTemplateType,
  });

  const queriedIds = useSelector((reduxState) =>
    getTemplatesListQueryIds(reduxState, { queryString: reduxStateString }),
  );
  const templates = useSelector(
    (reduxState) => (selectTemplatesByQuery as any)(reduxState, { queryString: reduxStateString }), // TODO: fix types, the second argument seems to not be used.
  );

  const totalNumberOfTemplates = useSelector((reduxState) =>
    getTemplatesListQueryTotals(reduxState, {
      queryString: reduxStateString,
    }),
  );

  const totalPages = Math.ceil(totalNumberOfTemplates / pageSize);

  useEffect(() => {
    // We only want to fetch from the backend if the templates
    // aren't already stored in redux
    // and we're not forcing a refresh
    // as needed in workflow studio for the
    // send node attach template takeover
    if (hasRenderedTemplatesInRedux(templates, queriedIds) && !forceRefresh) {
      return;
    }

    dispatch(
      fetchTemplates({
        filter,
        messageMedium,
        orderBy,
        page,
        pageSize,
        placementId,
        queryString: reduxStateString, // used during the saga
        sorting,
        templateType: selectedTab,
        whatsAppTemplateType,
      }),
    );
  }, [reduxStateString]);

  useEffect(() => {
    // whenever the query used to filter templates is updated
    // we have to reset the component to show the first page
    // so that we don't end up in a position where there's a blank page
    // because there are less total matching tiles
    // than the current page count would indicate
    setPage(1);
  }, [filter, templateTypeFilter]);

  const uniqueMemberIds = getUniqueMemberIds(templates);

  useEffect(() => {
    // in order to display the names and avatars of the members associated with the templates' memberId,
    // we need to a backend api call that fetches those users.
    if (uniqueMemberIds.length === 0) {
      return;
    }

    dispatch(fetchMemberData(uniqueMemberIds));
  }, [uniqueMemberIds.length]);

  const isFetchingTemplates = useSelector(getFetchingTemplates);

  if (isFetchingTemplates || !messageMedium) {
    return <Skeleton height={300} />;
  }

  return (
    <>
      <TemplateTiles
        campaignType={campaignType}
        messageMedium={messageMedium}
        onSelectDataFeed={onSelectDataFeed}
        onSelectTemplate={onSelectTemplate}
        query={filter}
        templateId={templateId}
        selectedTab={selectedTab}
        templates={templates}
        totalNumberOfTemplates={totalNumberOfTemplates}
        hasError={hasError}
        useDataFeeds={useDataFeeds}
      />
      <Footer>
        <Pagination onChangePage={(newPage) => setPage(newPage)} pages={totalPages} page={page} />
        <PageSize
          defaultSize={pageSize}
          onSelect={(newPageSize) => setPageSize(newPageSize)}
          pageSizes={[8, 12, 16, 32, 64, 128]}
          total={totalNumberOfTemplates}
        />
      </Footer>
    </>
  );
};
